import { useEffect, useState, useCallback } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  TrophyIcon,
  ClockIcon,
  StarIcon,
  UserGroupIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlayIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { Card, CardHeader, Badge, BadgeIcon, InlineSpinner, EmptyState, Button } from '@/components/ui'
import { tournamentApi } from '@/api/gamification'
import type {
  Tournament,
  TournamentQuizStart,
  TournamentQuizResult,
  TournamentLeaderboardEntry,
} from '@/api/gamification'

export default function TournamentPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()

  // List view state
  const [tournaments, setTournaments] = useState<Tournament[]>([])
  const [loadingList, setLoadingList] = useState(true)
  const [activeTab, setActiveTab] = useState<'available' | 'registered' | 'completed'>('available')

  // Detail view state
  const [tournament, setTournament] = useState<Tournament | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [leaderboard, setLeaderboard] = useState<TournamentLeaderboardEntry[]>([])
  const [userRank, setUserRank] = useState<number | undefined>()

  // Quiz state
  const [quizData, setQuizData] = useState<TournamentQuizStart | null>(null)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, number[]>>({})
  const [submitting, setSubmitting] = useState(false)
  const [quizResult, setQuizResult] = useState<TournamentQuizResult | null>(null)
  const [remainingTime, setRemainingTime] = useState(0)
  const [registering, setRegistering] = useState(false)

  // Timer for quiz
  useEffect(() => {
    let interval: NodeJS.Timeout
    if (quizData && remainingTime > 0 && !quizResult) {
      interval = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            // Auto-submit when time runs out
            handleSubmitQuiz()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [quizData, remainingTime, quizResult])

  useEffect(() => {
    if (slug) {
      fetchTournamentDetail()
    } else {
      fetchTournaments()
    }
  }, [slug])

  const fetchTournaments = async () => {
    setLoadingList(true)
    try {
      const data = await tournamentApi.getTournaments()
      setTournaments(data)
    } catch (error) {
      console.error('Error fetching tournaments:', error)
    } finally {
      setLoadingList(false)
    }
  }

  const fetchTournamentDetail = async () => {
    if (!slug) return
    setLoadingDetail(true)
    try {
      const [data, leaderboardData] = await Promise.all([
        tournamentApi.getTournament(slug),
        tournamentApi.getLeaderboard(slug).catch(() => ({ rankings: [], user_rank: undefined, total_participants: 0 }))
      ])
      setTournament(data)
      setLeaderboard(leaderboardData.rankings || [])
      setUserRank(leaderboardData.user_rank)
    } catch (error) {
      console.error('Error fetching tournament:', error)
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleRegister = async () => {
    if (!tournament) return
    setRegistering(true)
    try {
      await tournamentApi.register(tournament.slug)
      // Refresh tournament data
      await fetchTournamentDetail()
    } catch (error) {
      console.error('Error registering:', error)
    } finally {
      setRegistering(false)
    }
  }

  const handleStartQuiz = async () => {
    if (!tournament) return
    try {
      const data = await tournamentApi.startQuiz(tournament.slug)
      setQuizData(data)
      setRemainingTime(data.time_limit_seconds)
      setCurrentQuestionIndex(0)
      setSelectedAnswers({})
    } catch (error) {
      console.error('Error starting quiz:', error)
    }
  }

  const handleSelectAnswer = (questionId: number, answerId: number, isMultiple: boolean) => {
    setSelectedAnswers((prev) => {
      const key = questionId.toString()
      if (isMultiple) {
        const current = prev[key] || []
        if (current.includes(answerId)) {
          return { ...prev, [key]: current.filter((id) => id !== answerId) }
        }
        return { ...prev, [key]: [...current, answerId] }
      }
      return { ...prev, [key]: [answerId] }
    })
  }

  const handleSubmitQuiz = useCallback(async () => {
    if (!quizData || submitting) return
    setSubmitting(true)
    try {
      const result = await tournamentApi.submitQuiz(quizData.attempt_id, selectedAnswers)
      setQuizResult(result)
    } catch (error) {
      console.error('Error submitting quiz:', error)
    } finally {
      setSubmitting(false)
    }
  }, [quizData, selectedAnswers, submitting])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (status: Tournament['status'], is_joinable?: boolean) => {
    // Se lo status è 'registration' ma is_joinable è false, le iscrizioni sono chiuse
    if (status === 'registration' && is_joinable === false) {
      return { variant: 'default' as const, label: 'Iscrizioni chiuse' }
    }

    const config: Record<Tournament['status'], { variant: 'success' | 'warning' | 'info' | 'default' | 'danger'; label: string }> = {
      draft: { variant: 'default', label: 'Bozza' },
      registration: { variant: 'info', label: 'Iscrizioni aperte' },
      in_progress: { variant: 'warning', label: 'In corso' },
      completed: { variant: 'success', label: 'Completato' },
      archived: { variant: 'default', label: 'Archiviato' },
    }
    return config[status] || config.draft
  }

  const filteredTournaments = tournaments.filter((t) => {
    switch (activeTab) {
      case 'available':
        return ['registration', 'in_progress'].includes(t.status)
      case 'registered':
        return t.is_registered && !['completed', 'archived'].includes(t.status)
      case 'completed':
        return ['completed', 'archived'].includes(t.status) || (t.is_registered && t.my_participation?.status === 'completed')
      default:
        return true
    }
  })

  // List View
  if (!slug) {
    if (loadingList) {
      return <InlineSpinner message="Caricamento tornei..." />
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tornei Assessment</h1>
            <p className="text-gray-600">Competi con altri utenti e scala la classifica</p>
          </div>
          <Link
            to="/leaderboard"
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <TrophyIcon className="h-5 w-5 mr-2" />
            Classifica generale
          </Link>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            {[
              { key: 'available', label: 'Disponibili' },
              { key: 'registered', label: 'Iscritto' },
              { key: 'completed', label: 'Completati' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tournaments Grid */}
        {filteredTournaments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTournaments.map((t) => {
              const statusConfig = getStatusBadge(t.status, t.is_joinable)

              return (
                <Link key={t.id} to={`/tournaments/${t.slug}`} className="group">
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    {/* Header Image */}
                    <div className="relative h-40 -mx-4 -mt-4 mb-4 overflow-hidden rounded-t-lg bg-gradient-to-br from-amber-500 to-orange-700">
                      {t.cover_image ? (
                        <img src={t.cover_image} alt={t.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <TrophyIcon className="h-16 w-16 text-white/30" />
                        </div>
                      )}
                      <div className="absolute top-2 right-2">
                        <Badge variant={statusConfig.variant}>{statusConfig.label}</Badge>
                      </div>
                      {t.is_registered && (
                        <div className="absolute bottom-2 left-2">
                          <Badge variant="success">
                            <CheckCircleIcon className="h-3 w-3 mr-1" />
                            Iscritto
                          </Badge>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="space-y-3">
                      <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                        {t.title}
                      </h3>

                      <p className="text-sm text-gray-500 line-clamp-2">{t.description}</p>

                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <ClockIcon className="h-4 w-4" />
                          {t.time_limit_minutes} min
                        </span>
                        <span className="flex items-center gap-1">
                          <UserGroupIcon className="h-4 w-4" />
                          {t.participants_count} iscritti
                        </span>
                        <span className="flex items-center gap-1">
                          <StarIcon className="h-4 w-4" />
                          {t.questions_count} domande
                        </span>
                      </div>

                      {/* Dates */}
                      <div className="pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <CalendarIcon className="h-3 w-3" />
                          {t.status === 'registration'
                            ? t.is_joinable
                              ? `Iscrizioni fino al ${formatDate(t.registration_end)}`
                              : `Inizio torneo: ${formatDate(t.start_date)}`
                            : t.status === 'in_progress'
                            ? `Fino al ${formatDate(t.end_date)}`
                            : formatDate(t.start_date)}
                        </div>
                      </div>

                      {/* Prizes */}
                      <div className="flex items-center gap-3 pt-2">
                        <span className="text-xs text-gray-500">Premi:</span>
                        <span className="text-xs font-medium text-yellow-600">1° {t.points_reward_1st}pt</span>
                        <span className="text-xs font-medium text-gray-400">2° {t.points_reward_2nd}pt</span>
                        <span className="text-xs font-medium text-orange-500">3° {t.points_reward_3rd}pt</span>
                      </div>

                      {t.badge_winner && (
                        <div className="flex items-center gap-2 pt-2">
                          <BadgeIcon icon={t.badge_winner.icon} className="w-6 h-6" />
                          <span className="text-sm text-gray-600">Badge: {t.badge_winner.name}</span>
                        </div>
                      )}
                    </div>
                  </Card>
                </Link>
              )
            })}
          </div>
        ) : (
          <Card>
            <EmptyState
              icon={<TrophyIcon className="h-12 w-12" />}
              title={
                activeTab === 'available'
                  ? 'Nessun torneo disponibile'
                  : activeTab === 'registered'
                  ? 'Non sei iscritto a nessun torneo'
                  : 'Nessun torneo completato'
              }
              description={
                activeTab === 'available'
                  ? 'I tornei saranno disponibili a breve'
                  : activeTab === 'registered'
                  ? 'Iscriviti a un torneo per partecipare'
                  : 'Partecipa a un torneo per vedere i tuoi risultati'
              }
            />
          </Card>
        )}
      </div>
    )
  }

  // Detail/Quiz View
  if (loadingDetail) {
    return <InlineSpinner message="Caricamento torneo..." />
  }

  if (!tournament) {
    return (
      <EmptyState
        icon={<TrophyIcon className="h-12 w-12" />}
        title="Torneo non trovato"
        description="Il torneo richiesto non esiste"
        action={{
          label: 'Torna ai Tornei',
          onClick: () => navigate('/tournaments'),
        }}
      />
    )
  }

  const statusConfig = getStatusBadge(tournament.status, tournament.is_joinable)

  // Quiz in progress
  if (quizData && !quizResult) {
    const currentQuestion = quizData.questions[currentQuestionIndex]
    const isMultiple = currentQuestion?.type === 'multiple'
    const answeredCount = Object.keys(selectedAnswers).length

    return (
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Quiz Header */}
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">{tournament.title}</h1>
              <p className="text-sm text-gray-500">
                Domanda {currentQuestionIndex + 1} di {quizData.questions.length}
              </p>
            </div>
            <div className="text-right">
              <p className={`text-3xl font-mono font-bold ${remainingTime < 60 ? 'text-red-600' : 'text-primary-600'}`}>
                {formatTime(remainingTime)}
              </p>
              <p className="text-xs text-gray-500">Tempo rimanente</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestionIndex + 1) / quizData.questions.length) * 100}%` }}
            />
          </div>
        </Card>

        {/* Question */}
        <Card>
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-4">
                <Badge variant="info">{currentQuestion.points} punti</Badge>
                {isMultiple && <span className="text-sm text-gray-500">Selezione multipla</span>}
              </div>
              <h2 className="text-lg font-medium text-gray-900">{currentQuestion.text}</h2>
            </div>

            {/* Answers */}
            <div className="space-y-3">
              {currentQuestion.answers.map((answer) => {
                const isSelected = (selectedAnswers[currentQuestion.id.toString()] || []).includes(answer.id)
                return (
                  <button
                    key={answer.id}
                    onClick={() => handleSelectAnswer(currentQuestion.id, answer.id, isMultiple)}
                    className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 text-primary-900'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 rounded-${isMultiple ? 'sm' : 'full'} border-2 flex items-center justify-center ${
                          isSelected ? 'border-primary-500 bg-primary-500' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && <CheckCircleIcon className="h-3 w-3 text-white" />}
                      </div>
                      <span>{answer.text}</span>
                    </div>
                  </button>
                )
              })}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <Button
                variant="outline"
                onClick={() => setCurrentQuestionIndex((prev) => Math.max(0, prev - 1))}
                disabled={currentQuestionIndex === 0}
              >
                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                Precedente
              </Button>

              <span className="text-sm text-gray-500">
                {answeredCount}/{quizData.questions.length} risposte
              </span>

              {currentQuestionIndex < quizData.questions.length - 1 ? (
                <Button onClick={() => setCurrentQuestionIndex((prev) => prev + 1)}>
                  Successiva
                  <ChevronRightIcon className="h-5 w-5 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleSubmitQuiz} disabled={submitting}>
                  {submitting ? 'Invio...' : 'Termina Quiz'}
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Question Navigator */}
        <Card>
          <p className="text-sm font-medium text-gray-700 mb-3">Navigazione domande</p>
          <div className="flex flex-wrap gap-2">
            {quizData.questions.map((q, idx) => {
              const isAnswered = selectedAnswers[q.id.toString()]?.length > 0
              const isCurrent = idx === currentQuestionIndex
              return (
                <button
                  key={q.id}
                  onClick={() => setCurrentQuestionIndex(idx)}
                  className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                    isCurrent
                      ? 'bg-primary-600 text-white'
                      : isAnswered
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {idx + 1}
                </button>
              )
            })}
          </div>
        </Card>
      </div>
    )
  }

  // Quiz completed - Show results
  if (quizResult) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Card>
          <div className="text-center space-y-6">
            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center ${
              quizResult.score >= quizResult.max_score * 0.7 ? 'bg-green-100' : 'bg-yellow-100'
            }`}>
              <TrophyIcon className={`h-10 w-10 ${
                quizResult.score >= quizResult.max_score * 0.7 ? 'text-green-600' : 'text-yellow-600'
              }`} />
            </div>

            <div>
              <h1 className="text-2xl font-bold text-gray-900">Quiz Completato!</h1>
              <p className="text-gray-600 mt-2">Hai completato "{tournament.title}"</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-primary-600">
                  {quizResult.score}/{quizResult.max_score}
                </p>
                <p className="text-sm text-gray-500">Punteggio</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-600">{formatTime(quizResult.time_taken_seconds)}</p>
                <p className="text-sm text-gray-500">Tempo impiegato</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">
                  {quizResult.correct_answers}/{quizResult.total_questions}
                </p>
                <p className="text-sm text-gray-500">Risposte corrette</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-amber-600">+{quizResult.points_earned}</p>
                <p className="text-sm text-gray-500">Punti guadagnati</p>
              </div>
            </div>

            {quizResult.rank && (
              <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                <p className="text-lg font-semibold text-primary-700">
                  Posizione attuale: {quizResult.rank}°
                </p>
              </div>
            )}

            <div className="flex gap-4 justify-center pt-4">
              <Link
                to="/tournaments"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Torna ai Tornei
              </Link>
              <Link
                to={`/tournaments/${tournament.slug}`}
                onClick={() => {
                  setQuizData(null)
                  setQuizResult(null)
                  fetchTournamentDetail()
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Vedi classifica
              </Link>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  // Detail View (not started)
  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-500">
        <Link to="/tournaments" className="hover:text-primary-600">
          Tornei
        </Link>
        <ChevronRightIcon className="h-4 w-4" />
        <span className="text-gray-900">{tournament.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            {/* Header Image */}
            <div className="relative h-64 -mx-4 -mt-4 mb-6 overflow-hidden rounded-t-lg bg-gradient-to-br from-amber-500 to-orange-700">
              {tournament.cover_image ? (
                <img src={tournament.cover_image} alt={tournament.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <TrophyIcon className="h-24 w-24 text-white/30" />
                </div>
              )}
              <div className="absolute top-4 right-4">
                <Badge variant={statusConfig.variant} size="lg">
                  {statusConfig.label}
                </Badge>
              </div>
              {tournament.is_registered && (
                <div className="absolute bottom-4 left-4">
                  <Badge variant="success" size="lg">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    Iscritto
                  </Badge>
                </div>
              )}
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-4">{tournament.title}</h1>
            <p className="text-gray-600 whitespace-pre-line">{tournament.description}</p>

            {tournament.rules && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Regolamento</h3>
                <p className="text-sm text-gray-600 whitespace-pre-line">{tournament.rules}</p>
              </div>
            )}

            {/* Stats */}
            <div className="flex flex-wrap gap-6 mt-6 pt-6 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <ClockIcon className="h-5 w-5 text-gray-400" />
                <span className="text-gray-600">{tournament.time_limit_minutes} minuti</span>
              </div>
              <div className="flex items-center gap-2">
                <StarIcon className="h-5 w-5 text-gray-400" />
                <span className="text-gray-600">{tournament.questions_count} domande</span>
              </div>
              <div className="flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5 text-gray-400" />
                <span className="text-gray-600">
                  {tournament.participants_count}
                  {tournament.max_participants && ` / ${tournament.max_participants}`} partecipanti
                </span>
              </div>
              <div className="flex items-center gap-2">
                <ArrowPathIcon className="h-5 w-5 text-gray-400" />
                <span className="text-gray-600">{tournament.max_attempts} tentativi</span>
              </div>
            </div>
          </Card>

          {/* Leaderboard */}
          {leaderboard.length > 0 && (
            <Card>
              <CardHeader title="Classifica" />
              <div className="space-y-2">
                {leaderboard.slice(0, 10).map((entry, index) => (
                  <div
                    key={entry.user_id}
                    className={`flex items-center gap-4 p-3 rounded-lg ${
                      index < 3 ? 'bg-primary-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span
                      className={`w-8 text-center font-bold ${
                        index === 0
                          ? 'text-yellow-500'
                          : index === 1
                          ? 'text-gray-400'
                          : index === 2
                          ? 'text-orange-500'
                          : 'text-gray-400'
                      }`}
                    >
                      {entry.rank}
                    </span>
                    <span className="flex-1 font-medium text-gray-900">{entry.display_name}</span>
                    <span className="font-bold text-primary-600">{entry.best_score} pt</span>
                    {entry.best_time_seconds && (
                      <span className="text-sm text-gray-500">{formatTime(entry.best_time_seconds)}</span>
                    )}
                  </div>
                ))}
              </div>
              {userRank && userRank > 10 && (
                <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                  <p className="text-sm text-gray-600">
                    La tua posizione: <span className="font-bold text-primary-600">{userRank}°</span>
                  </p>
                </div>
              )}
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Action Card */}
          <Card>
            <div className="text-center space-y-4">
              {tournament.status === 'registration' && !tournament.is_registered ? (
                tournament.is_joinable ? (
                  <>
                    <div className="w-16 h-16 mx-auto bg-primary-100 rounded-full flex items-center justify-center">
                      <UserGroupIcon className="h-8 w-8 text-primary-600" />
                    </div>
                    <p className="text-gray-600">Iscrizioni aperte fino al {formatDate(tournament.registration_end)}</p>
                    <Button className="w-full" onClick={handleRegister} disabled={registering}>
                      {registering ? 'Iscrizione...' : 'Iscriviti al torneo'}
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                      <ClockIcon className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-gray-600">Iscrizioni chiuse</p>
                    <p className="text-sm text-gray-500">Il torneo inizierà il {formatDate(tournament.start_date)}</p>
                  </>
                )
              ) : tournament.is_registered && ['registration', 'in_progress'].includes(tournament.status) ? (
                <>
                  <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircleIcon className="h-8 w-8 text-green-600" />
                  </div>
                  <p className="text-green-600 font-medium">Sei iscritto a questo torneo</p>
                  {tournament.status === 'in_progress' && (
                    <>
                      {tournament.my_participation && tournament.my_participation.attempts_count >= tournament.max_attempts ? (
                        <p className="text-sm text-gray-500">Hai esaurito i tentativi disponibili</p>
                      ) : (
                        <Button className="w-full" onClick={handleStartQuiz}>
                          <PlayIcon className="h-5 w-5 mr-2" />
                          {tournament.my_participation?.attempts_count ? 'Nuovo tentativo' : 'Inizia il quiz'}
                        </Button>
                      )}
                      {tournament.my_participation?.best_score !== undefined && (
                        <p className="text-sm text-gray-500">
                          Miglior punteggio: <span className="font-bold">{tournament.my_participation.best_score}</span>
                        </p>
                      )}
                    </>
                  )}
                  {tournament.status === 'registration' && (
                    <p className="text-sm text-gray-500">Il torneo inizierà il {formatDate(tournament.start_date)}</p>
                  )}
                </>
              ) : tournament.status === 'completed' ? (
                <>
                  <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                    <TrophyIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-medium">Torneo concluso</p>
                  {tournament.my_participation?.final_rank && (
                    <p className="text-lg">
                      Posizione finale: <span className="font-bold text-primary-600">{tournament.my_participation.final_rank}°</span>
                    </p>
                  )}
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                    <XCircleIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500">Le iscrizioni sono chiuse</p>
                </>
              )}
            </div>
          </Card>

          {/* Prizes Card */}
          <Card>
            <CardHeader title="Premi" />
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🥇</span>
                  <span className="font-medium">1° posto</span>
                </div>
                <span className="font-bold text-yellow-700">{tournament.points_reward_1st} pt</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🥈</span>
                  <span className="font-medium">2° posto</span>
                </div>
                <span className="font-bold text-gray-600">{tournament.points_reward_2nd} pt</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🥉</span>
                  <span className="font-medium">3° posto</span>
                </div>
                <span className="font-bold text-orange-600">{tournament.points_reward_3rd} pt</span>
              </div>
              {tournament.points_participation > 0 && (
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <span className="font-medium">Partecipazione</span>
                  <span className="font-bold text-blue-600">{tournament.points_participation} pt</span>
                </div>
              )}
            </div>
          </Card>

          {/* Badge Reward */}
          {tournament.badge_winner && (
            <Card>
              <CardHeader title="Badge Vincitore" />
              <div className="text-center">
                <BadgeIcon icon={tournament.badge_winner.icon} className="w-16 h-16 mx-auto" />
                <p className="font-medium text-gray-900 mt-2">{tournament.badge_winner.name}</p>
                <p className="text-sm text-gray-500 mt-1">{tournament.badge_winner.description}</p>
              </div>
            </Card>
          )}

          {/* Dates Info */}
          <Card>
            <CardHeader title="Date" />
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">Inizio iscrizioni</dt>
                <dd className="font-medium text-gray-900">{formatDate(tournament.registration_start)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Fine iscrizioni</dt>
                <dd className="font-medium text-gray-900">{formatDate(tournament.registration_end)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Inizio torneo</dt>
                <dd className="font-medium text-gray-900">{formatDate(tournament.start_date)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Fine torneo</dt>
                <dd className="font-medium text-gray-900">{formatDate(tournament.end_date)}</dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>
    </div>
  )
}
